import type { AuthenticationResult } from '@azure/msal-browser';

/**
 * The invitation a person is in the middle of accepting (M2-20, pass B), kept
 * across the trip to Entra and back.
 *
 * <b>Why this exists.</b> MSAL returns to ONE registered redirect URI, the
 * app's root (`msalConfig.auth.redirectUri`), and MSAL v5 no longer navigates
 * back to the page that started the sign-in. So the invite page saves its
 * token here before it sends the person to sign in, and `AuthProvider` puts
 * the tab back on `/invite` when the redirect it handles is one this page
 * started. No new redirect URI is needed, and no Entra registration changes.
 *
 * <b>Why the token never goes back in the URL.</b> `/invite/{account}.{secret}`
 * is a key, not an identifier (see `lib/telemetryRedaction.ts`): whoever holds
 * it can create the invitee's sign-in and see which company invited them. The
 * page moves it here the moment it arrives and continues at a bare `/invite`,
 * so the address bar, the history entry, every later page view, the path MSAL
 * records as where the sign-in started, and the `state` sent to Entra all
 * carry nothing. Only the first page view sees it, and the telemetry redactor
 * replaces it there.
 *
 * <b>sessionStorage</b>, like MSAL's own cache: per tab, gone when the tab
 * closes, never shared with another tab on an office PC. Every access is
 * guarded — storage can be unavailable (a locked-down browser), and losing the
 * resume is better than throwing during sign-in.
 */

const KEY = 'mlp.invite';

/** The `state` the invite page's sign-in carries to Entra and back. Nothing secret: a marker. */
export const INVITE_LOGIN_STATE = 'mlp-invite';

/** Where the tab continues after the token leaves the URL, and after sign-in. */
export const INVITE_RESUME_PATH = '/invite';

export interface PendingInvite {
  token: string;
  /** Set just before the page sends the person to Entra; cleared when it accepts. */
  signingIn: boolean;
}

/** Saves the token from the link. False when storage refused it (the page then keeps it in the URL). */
export function savePendingInvite(token: string): boolean {
  return write({ token, signingIn: false });
}

export function readPendingInvite(): PendingInvite | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingInvite>;
    return typeof parsed.token === 'string' && parsed.token !== ''
      ? { token: parsed.token, signingIn: parsed.signingIn === true }
      : null;
  } catch {
    return null;
  }
}

/** Marks that the page is about to leave for Entra, so the return can pick up where it left off. */
export function markSigningIn(): void {
  const pending = readPendingInvite();
  if (pending) write({ ...pending, signingIn: true });
}

/** The return from Entra has been handled: accept once, not on every reload. */
export function clearSigningIn(): void {
  const pending = readPendingInvite();
  if (pending) write({ ...pending, signingIn: false });
}

export function clearPendingInvite(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do: storage that cannot be read holds nothing to clear.
  }
}

/**
 * Called by `AuthProvider` once MSAL has handled the redirect, BEFORE the
 * router mounts: puts the tab on `/invite` when the sign-in that just returned
 * was the invite page's — or when a sign-in it started came back as an error
 * (the person cancelled at Entra), so the page can say so instead of leaving
 * them on the office's dashboard.
 *
 * Returns whether it moved the tab, for the tests.
 */
export function resumeInviteAfterRedirect(
  response: AuthenticationResult | null,
  redirectFailed: boolean,
): boolean {
  const started = response?.state === INVITE_LOGIN_STATE;
  const abandoned = redirectFailed && readPendingInvite()?.signingIn === true;
  if (!started && !abandoned) return false;
  window.history.replaceState(null, '', INVITE_RESUME_PATH);
  return true;
}

function write(value: PendingInvite): boolean {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
